import { useState, useCallback, useEffect, useRef } from "react";
import { EmailRecipient, PermissionLevel } from "../types";

export const useEmailRecipients = (initialRecipients: EmailRecipient[] = [], defaultPermissionLevel: PermissionLevel = "Can Pay") => {
  const [emailRecipients, setEmailRecipients] = useState<EmailRecipient[]>(initialRecipients);
  const previousInitialRef = useRef<string>("");

  // Sync state when initialRecipients change (e.g., when client data loads)
  // Only update if current recipients are empty and initialRecipients has data
  useEffect(() => {
    const currentInitialKey = JSON.stringify(initialRecipients);
    const hasInitialRecipients = initialRecipients.length > 0;
    
    // Update if:
    // 1. We have initial recipients
    // 2. Initial recipients changed (different from previous)
    // 3. Current list is empty (to avoid overwriting user changes)
    if (hasInitialRecipients && currentInitialKey !== previousInitialRef.current) {
      setEmailRecipients((prev) => {
        // Only update if current list is empty to avoid overwriting user changes
        if (prev.length === 0) {
          previousInitialRef.current = currentInitialKey;
          return initialRecipients;
        }
        return prev;
      });
    }
  }, [initialRecipients]);

  const addRecipient = useCallback(() => {
    setEmailRecipients((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        email: "",
        permissionLevel: defaultPermissionLevel,
      },
    ]);
  }, [defaultPermissionLevel]);

  const deleteRecipient = useCallback((id: string) => {
    setEmailRecipients((prev) => prev.filter((recipient) => recipient.id !== id));
  }, []);

  const updateRecipient = useCallback((id: string, field: keyof EmailRecipient, value: any) => {
    setEmailRecipients((prev) =>
      prev.map((recipient) => (recipient.id === id ? { ...recipient, [field]: value } : recipient))
    );
  }, []);

  const setAllPermissionLevels = useCallback((permissionLevel: PermissionLevel) => {
    setEmailRecipients((prev) =>
      prev.map((recipient) => (recipient.permissionLevel === permissionLevel ? recipient : { ...recipient, permissionLevel })),
    );
  }, []);

  return {
    emailRecipients,
    addRecipient,
    deleteRecipient,
    updateRecipient,
    setAllPermissionLevels,
  };
};
